import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Send, Upload, FileText, Image as ImageIcon, Film, Music,
  Mic, Lock, Star, X, AlertCircle, User as UserIcon, Calendar,
} from "lucide-react";
import VoiceRecorder from "@/components/VoiceRecorder";
import VoicePlayer from "@/components/VoicePlayer";

const STATUS_TONES = {
  new: "bg-blue-50 text-blue-700 border-blue-200",
  awaiting_approval: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  assigned: "bg-indigo-50 text-indigo-700 border-indigo-200",
  in_progress: "bg-purple-50 text-purple-700 border-purple-200",
  pending_info: "bg-orange-50 text-orange-700 border-orange-200",
  pending_purchase: "bg-orange-50 text-orange-700 border-orange-200",
  ready_for_collection: "bg-teal-50 text-teal-700 border-teal-200",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-slate-50 text-slate-700 border-slate-200",
  cancelled: "bg-slate-50 text-slate-500 border-slate-200",
  on_hold: "bg-amber-50 text-amber-700 border-amber-200",
};

const HANDLER_ROLES = ["super_admin", "hr", "ea", "manager", "hod", "senior_management"];
const STATUS_OPTIONS = [
  "new", "in_progress", "pending_info", "pending_purchase",
  "ready_for_collection", "resolved", "closed", "on_hold", "cancelled",
];

function fileIcon(mime) {
  if (!mime) return FileText;
  if (mime.startsWith("image/")) return ImageIcon;
  if (mime.startsWith("video/")) return Film;
  if (mime.startsWith("audio/")) return Music;
  return FileText;
}

export default function RequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [replyVoice, setReplyVoice] = useState(null);
  const [replyAtt, setReplyAtt] = useState([]);
  const [replyType, setReplyType] = useState("comment");
  const [isInternal, setIsInternal] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState("");

  const refresh = async () => {
    try {
      const { data: d } = await api.get(`/requests/${id}`);
      setData(d);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load request");
    }
  };

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => {});
    refresh();
    // Mark as read (Phase 3)
    api.post(`/requests/${id}/mark-read`).catch(() => {});
    // eslint-disable-next-line
  }, [id]);

  if (!data) return <div className="text-sm text-slate-500 p-6">Loading…</div>;

  const isOwner = me && me.id === data.employee_id;
  const isHandler = me && HANDLER_ROLES.includes(me.role);

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: rec } = await api.post("/requests/attachments", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReplyAtt((prev) => [...prev, rec]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    }
  };

  const send = async () => {
    if (!reply.trim() && !replyVoice && replyAtt.length === 0 && !newStatus) {
      return toast.error("Add text, voice, attachments, or status change");
    }
    setSubmitting(true);
    try {
      await api.post(`/requests/${id}/replies`, {
        reply_text: reply.trim() || null,
        voice_note_url: replyVoice || null,
        attachment_ids: replyAtt.map((a) => a.id),
        reply_type: replyType,
        is_internal: isHandler ? isInternal : false,
        new_status: isHandler && newStatus ? newStatus : null,
      });
      setReply("");
      setReplyVoice(null);
      setReplyAtt([]);
      setIsInternal(false);
      setNewStatus("");
      setReplyType("comment");
      toast.success("Reply sent");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    } finally {
      setSubmitting(false);
    }
  };

  const submitFeedback = async () => {
    if (!rating) return toast.error("Please pick a star rating");
    try {
      await api.post(`/requests/${id}/feedback`, { rating, comment: feedbackText.trim() || null });
      toast.success("Thanks for the feedback");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Submit failed");
    }
  };

  const replies = data.replies || [];
  const ownAtts = data.attachments || [];
  const isResolved = ["resolved", "closed"].includes(data.status);

  return (
    <div className="max-w-4xl mx-auto space-y-4" data-testid="request-detail">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back
        </Button>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">{data.request_number}</div>
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>{data.subject}</h1>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-3">
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full border font-medium ${STATUS_TONES[data.status] || ""}`} data-testid="status-pill">
            {(data.status || "").replace(/_/g, " ")}
          </span>
          <span className="text-slate-500">Priority: <b className="text-slate-700">{data.priority}</b></span>
          <span className="text-slate-500">Type: <b className="text-slate-700">{data.request_type?.replace("_", " ")}</b></span>
          {data.category && <span className="text-slate-500">Category: <b className="text-slate-700">{data.category}</b></span>}
          {data.estimated_cost ? (
            <span className="text-slate-500">Est. Cost: <b className="text-slate-700">₹{Number(data.estimated_cost).toLocaleString("en-IN")}</b></span>
          ) : null}
          {data.expected_resolution_time && (
            <span className="text-slate-500">SLA: <b className="text-slate-700">{new Date(data.expected_resolution_time).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</b></span>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs pt-2 border-t border-slate-100">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Raised by</div>
            <div className="text-slate-700 mt-0.5 flex items-center gap-1"><UserIcon className="h-3 w-3" /> {data.employee_name}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Created</div>
            <div className="text-slate-700 mt-0.5 flex items-center gap-1">
              <Calendar className="h-3 w-3" /> {new Date(data.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>
          {data.assigned_team && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Team</div>
              <div className="text-slate-700 mt-0.5">{data.assigned_team.replace("_", " ")}</div>
            </div>
          )}
          {data.approval_required && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Approval</div>
              <div className="text-slate-700 mt-0.5 text-xs">{(data.approval_chain || []).join(" → ") || data.cost_category}</div>
            </div>
          )}
        </div>

        {/* IT specs */}
        {data.specs && Object.keys(data.specs).length > 0 && (
          <div className="border border-blue-100 bg-blue-50/40 rounded p-2 text-xs" data-testid="specs-block">
            <div className="font-semibold text-blue-900 mb-1">Specifications</div>
            <div className="grid grid-cols-2 gap-1 text-slate-700">
              {Object.entries(data.specs).map(([k, v]) => v && (
                <div key={k}><b className="capitalize">{k.replace(/_/g, " ")}:</b> {v}</div>
              ))}
            </div>
          </div>
        )}

        {/* Stationary items */}
        {data.items && data.items.length > 0 && (
          <div className="border border-amber-100 bg-amber-50/40 rounded p-2 text-xs" data-testid="items-block">
            <div className="font-semibold text-amber-900 mb-1">Items Requested</div>
            <table className="w-full">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                <tr><th className="text-left">Item</th><th className="text-right">Qty</th><th className="text-right">₹/unit</th><th className="text-left pl-2">Specs</th></tr>
              </thead>
              <tbody>
                {data.items.map((it, idx) => (
                  <tr key={idx} className="border-t border-amber-100">
                    <td className="py-1">{it.name}</td>
                    <td className="text-right">{it.quantity}</td>
                    <td className="text-right">{it.estimated_cost_per_unit ? `₹${it.estimated_cost_per_unit}` : "—"}</td>
                    <td className="pl-2 text-slate-500">{it.specifications || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data.justification && (
          <div className="border-l-2 border-amber-400 bg-amber-50/30 pl-3 py-2 text-xs">
            <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Justification</div>
            <div className="text-slate-700 mt-0.5 whitespace-pre-wrap">{data.justification}</div>
          </div>
        )}

        {/* Approval chain status (Phase 2) */}
        {data.approval_required && (
          <div className="border border-slate-200 rounded p-3 space-y-1.5" data-testid="approval-status-panel">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Approval Chain</div>
            {(data.approval_chain || []).map((lvl) => {
              const st = data[`${lvl}_status`];
              const ts = data[`${lvl}_action_at`];
              const remarks = data[`${lvl}_remarks`];
              const tone =
                st === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : st === "rejected" ? "bg-red-50 text-red-700 border-red-200"
                : st === "pending" ? "bg-amber-50 text-amber-700 border-amber-200"
                : "bg-slate-50 text-slate-500 border-slate-200";
              const Icon = st === "approved" ? "✅" : st === "rejected" ? "❌" : st === "pending" ? "⏳" : "⏸️";
              return (
                <div key={lvl} className={`flex items-start gap-2 px-2 py-1.5 rounded border text-xs ${tone}`} data-testid={`approval-${lvl}-${st || "na"}`}>
                  <span className="font-semibold uppercase">{Icon} {lvl.replace("_", " ")}</span>
                  <span className="capitalize">{st || "—"}</span>
                  {ts && <span className="text-slate-500 ml-auto">{new Date(ts).toLocaleString("en-IN", { day: "2-digit", month: "short" })}</span>}
                  {remarks && <span className="text-slate-700 italic w-full mt-0.5">"{remarks}"</span>}
                </div>
              );
            })}
            {data.approval_conditions && data.approval_conditions.length > 0 && (
              <div className="text-xs mt-2 bg-emerald-50/60 border border-emerald-200 rounded p-2">
                <div className="font-semibold text-emerald-800 mb-1">Conditions / Instructions:</div>
                <ul className="list-disc list-inside text-slate-700">
                  {data.approval_conditions.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Original attachments */}
        {ownAtts.length > 0 && (
          <div className="text-xs">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Attachments</div>
            <div className="flex flex-wrap gap-1.5">
              {ownAtts.map((a) => {
                const Icon = fileIcon(a.file_type);
                return (
                  <a key={a.id} href={a.file_url} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1.5 px-2 py-1 bg-slate-50 border border-slate-200 rounded text-slate-700 hover:bg-slate-100">
                    <Icon className="h-3 w-3" /> {a.file_name}
                  </a>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Conversation thread */}
      <div className="space-y-2" data-testid="thread">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Conversation</div>
        {replies.map((c) => {
          const mine = me && c.replied_by === me.id;
          const fromHandler = c.replier_role && c.replier_role !== "employee";
          return (
            <div key={c.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`reply-${c.id}`}>
              <div className={`max-w-[80%] rounded-lg border p-3 ${
                c.is_internal ? "bg-yellow-50 border-yellow-200"
                : mine ? "bg-red-50/60 border-red-200"
                : fromHandler ? "bg-blue-50/40 border-blue-200"
                : "bg-white border-slate-200"
              }`}>
                <div className="flex items-center gap-2 mb-1 text-[10px] uppercase tracking-wider text-slate-500">
                  <b className="text-slate-700 normal-case">{c.replier_name || "—"}</b>
                  <span>· {c.replier_role?.replace(/_/g, " ")}</span>
                  {c.is_internal && <span className="inline-flex items-center gap-0.5 text-yellow-700"><Lock className="h-2.5 w-2.5" /> Internal</span>}
                  <span className="ml-auto">{new Date(c.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                </div>
                {c.reply_text && <div className="text-sm text-slate-700 whitespace-pre-wrap">{c.reply_text}</div>}
                {c.voice_note_url && (
                  <div className="mt-2 flex items-center gap-2 bg-white border border-slate-200 rounded px-2 py-1.5">
                    <Mic className="h-3.5 w-3.5 text-red-600 flex-shrink-0" />
                    <VoicePlayer src={c.voice_note_url} className="h-7 max-w-[260px]" data-testid={`reply-voice-${c.id}`} />
                  </div>
                )}
                {c.attachment_ids && c.attachment_ids.length > 0 && data.attachments && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {data.attachments.filter((a) => c.attachment_ids.includes(a.id)).map((a) => {
                      const Icon = fileIcon(a.file_type);
                      return (
                        <a key={a.id} href={a.file_url} target="_blank" rel="noreferrer"
                           className="inline-flex items-center gap-1.5 px-2 py-1 bg-white border border-slate-200 rounded text-xs text-slate-700 hover:bg-slate-50">
                          <Icon className="h-3 w-3" /> {a.file_name}
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Reply composer */}
      {!isResolved && (
        <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-2" data-testid="reply-composer">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Add Reply</div>
          <Textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            rows={3}
            placeholder={isHandler ? "Reply to employee or add internal note…" : "Type your reply…"}
            data-testid="reply-textarea"
          />
          <VoiceRecorder
            value={replyVoice}
            onChange={(url) => setReplyVoice(url || null)}
            uploadEndpoint="/requests/attachments"
            uploadFields={{}}
            urlFromResponse={(d) => d.file_url}
            testIdPrefix="reply-voice"
            compact
          />
          {replyAtt.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {replyAtt.map((a) => (
                <div key={a.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-50 border border-slate-200 rounded text-xs">
                  {a.file_name}
                  <button onClick={() => setReplyAtt((prev) => prev.filter((x) => x.id !== a.id))} className="text-red-700">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-1.5 px-2 py-1 text-xs border border-slate-200 rounded cursor-pointer hover:bg-slate-50">
              <Upload className="h-3.5 w-3.5" /> Attach
              <input type="file" className="hidden" multiple onChange={(e) => {
                Array.from(e.target.files || []).forEach(uploadFile);
                e.target.value = "";
              }} data-testid="reply-attach-input" />
            </label>

            {isHandler && (
              <>
                <Select value={replyType} onValueChange={setReplyType}>
                  <SelectTrigger className="w-32 h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="comment">Comment</SelectItem>
                    <SelectItem value="question">Question</SelectItem>
                    <SelectItem value="answer">Answer</SelectItem>
                    <SelectItem value="update">Update</SelectItem>
                    <SelectItem value="clarification">Clarification</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={newStatus || "_"} onValueChange={(v) => setNewStatus(v === "_" ? "" : v)}>
                  <SelectTrigger className="w-44 h-7 text-xs" data-testid="status-select"><SelectValue placeholder="Keep status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_">Keep status</SelectItem>
                    {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
                <label className="inline-flex items-center gap-1 text-xs text-slate-600">
                  <input type="checkbox" checked={isInternal} onChange={(e) => setIsInternal(e.target.checked)} className="accent-amber-600" data-testid="internal-checkbox" />
                  <Lock className="h-3 w-3" /> Internal note
                </label>
              </>
            )}

            <Button
              onClick={send}
              disabled={submitting || (!reply.trim() && !replyVoice && replyAtt.length === 0 && !newStatus)}
              className="ml-auto h-7 text-xs brand-bg brand-bg-hover text-white"
              data-testid="send-reply-btn"
            >
              <Send className="h-3 w-3 mr-1" /> {submitting ? "Sending…" : "Send"}
            </Button>
          </div>
        </div>
      )}

      {/* Feedback */}
      {isResolved && isOwner && data.employee_satisfaction == null && (
        <div className="bg-emerald-50/40 border border-emerald-200 rounded-lg p-4" data-testid="feedback-block">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Rate this service</div>
          <div className="text-sm text-slate-700 mt-1">How was your experience?</div>
          <div className="flex items-center gap-1 mt-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} onClick={() => setRating(n)} data-testid={`rating-${n}`}>
                <Star className={`h-6 w-6 ${rating >= n ? "fill-yellow-400 text-yellow-400" : "text-slate-300"}`} />
              </button>
            ))}
          </div>
          <Textarea
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            rows={2}
            placeholder="Any feedback (optional)?"
            className="mt-2"
            data-testid="feedback-text"
          />
          <Button onClick={submitFeedback} className="mt-2 brand-bg brand-bg-hover text-white h-7 text-xs" data-testid="submit-feedback-btn">
            Submit feedback
          </Button>
        </div>
      )}
      {isResolved && data.employee_satisfaction && (
        <div className="bg-emerald-50/40 border border-emerald-200 rounded-lg p-3 text-xs flex items-center gap-2">
          <span className="text-emerald-700 font-semibold">Feedback recorded</span>
          <span className="flex items-center gap-0.5">
            {[1,2,3,4,5].map((n) => (
              <Star key={n} className={`h-3.5 w-3.5 ${data.employee_satisfaction >= n ? "fill-yellow-400 text-yellow-400" : "text-slate-300"}`} />
            ))}
          </span>
          {data.employee_feedback && <span className="text-slate-600">— {data.employee_feedback}</span>}
        </div>
      )}
    </div>
  );
}
